import * as React from 'react'

import { useKeyboardShortcut } from '~publish/hooks/useKeyboardShortcut'

import type { PostListItem } from '../types'

/**
 * Registers Cmd+Up / Cmd+Down keyboard shortcuts to navigate between posts
 * in the "By Post" comments view.
 *
 * Shortcuts are automatically disabled when an input, textarea,
 * or contentEditable element is focused (via useKeyboardShortcut defaults).
 */
export function usePostKeyboardNavigation({
  posts,
  selectedPostId,
  onPostSelect,
}: {
  posts: PostListItem[]
  selectedPostId?: string
  onPostSelect: (postId: string) => void
}): void {
  const disabled = !selectedPostId || posts.length === 0

  const currentIndex = React.useMemo(
    () => posts.findIndex((p) => p.resolved.id === selectedPostId),
    [posts, selectedPostId],
  )

  const navigatePrev = React.useCallback(() => {
    if (currentIndex > 0) {
      onPostSelect(posts[currentIndex - 1].resolved.id)
    }
  }, [currentIndex, posts, onPostSelect])

  const navigateNext = React.useCallback(() => {
    if (currentIndex !== -1 && currentIndex < posts.length - 1) {
      onPostSelect(posts[currentIndex + 1].resolved.id)
    }
  }, [currentIndex, posts, onPostSelect])

  useKeyboardShortcut('mod+ArrowUp', navigatePrev, { disabled })
  useKeyboardShortcut('mod+ArrowDown', navigateNext, { disabled })
}
